import React, { useState, useEffect } from "react";

const LoanEligibilityCalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(100000);
  const [existingEMI, setExistingEMI] = useState(15000);
  const [loanTenure, setLoanTenure] = useState(20);
  const [tenureType, setTenureType] = useState("years");
  const [interestRate, setInterestRate] = useState(8.5);
  const [foirPercentage, setFoirPercentage] = useState(50);
  const [result, setResult] = useState({
    maxEMI: "0.00",
    eligibleLoanAmount: "0.00",
    totalPayment: "0.00",
    totalInterest: "0.00",
    availableEMI: "0.00",
    utilizationPercentage: "0",
  });

  // Loan Eligibility Calculation Function
  const calculateEligibility = () => {
    const income = parseFloat(monthlyIncome);
    const existingEmi = parseFloat(existingEMI);
    const foir = parseFloat(foirPercentage) / 100;
    const annual_rate = parseFloat(interestRate) / 100;
    const r = annual_rate / 12;
    let tenure = parseFloat(loanTenure);

    if (isNaN(income) || isNaN(foir) || isNaN(r) || isNaN(tenure) || income <= 0 || tenure <= 0) return;

    const n = tenureType === "years" ? tenure * 12 : tenure;

    // Calculate maximum EMI based on FOIR
    const maxAllowedEMI = income * foir;
    const availableEMI = Math.max(0, maxAllowedEMI - existingEmi);

    // Calculate eligible loan amount using EMI formula reversed
    // EMI = [P × r × (1 + r)^n] / [(1 + r)^n - 1]
    // P = EMI × [(1 + r)^n - 1] / [r × (1 + r)^n]
    let eligibleLoan = 0;
    if (r > 0) {
      eligibleLoan = availableEMI * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n));
    } else {
      eligibleLoan = availableEMI * n;
    }

    // Calculate total payment and interest
    const totalPayment = availableEMI * n;
    const totalInterest = totalPayment - eligibleLoan;

    // Calculate utilization
    const utilizationPercentage = existingEmi > 0 ? ((existingEmi / maxAllowedEMI) * 100) : 0;

    setResult({
      maxEMI: maxAllowedEMI.toFixed(2),
      eligibleLoanAmount: Math.max(0, eligibleLoan).toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      totalInterest: Math.max(0, totalInterest).toFixed(2),
      availableEMI: availableEMI.toFixed(2),
      utilizationPercentage: utilizationPercentage.toFixed(1),
    });
  };

  useEffect(() => {
  calculateEligibility();
}, [monthlyIncome, existingEMI, loanTenure, tenureType, interestRate, foirPercentage]);

  const tenureLabel = tenureType === "years" ? "Years" : "Months";

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <polyline points="16 11 18 13 22 9"></polyline>
            </svg>
          </div>
          <h1>Loan Eligibility Calculator</h1>
          <p>
            Calculate how much loan you can get based on your monthly income and existing obligations
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Income Details
              </h2>

              {/* Monthly Income */}
              <div className="input-group">
                <label>Monthly Income</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={monthlyIncome}
                    onChange={(e) => setMonthlyIncome(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 150000, 200000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setMonthlyIncome(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Existing EMI */}
              <div className="input-group">
                <label>Existing EMI/Obligations</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={existingEMI}
                    onChange={(e) => setExistingEMI(e.target.value)}
                    className="calc-input"
                    placeholder="15000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 10000, 20000, 30000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setExistingEMI(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Loan Tenure */}
              <div className="input-group">
                <label>Desired Loan Tenure</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(e.target.value)}
                    className="calc-input"
                    placeholder="20"
                  />
                </div>
                <div className="quick-select-group">
                  {tenureType === "years" 
                    ? [5, 10, 15, 20, 30].map((years) => (
                        <button
                          key={years}
                          onClick={() => setLoanTenure(years)}
                          className="quick-select-btn success"
                        >
                          {years}Y
                        </button>
                      ))
                    : [12, 24, 36, 60].map((months) => (
                        <button
                          key={months}
                          onClick={() => setLoanTenure(months)}
                          className="quick-select-btn success"
                        >
                          {months}M
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Tenure Type */}
              <div className="input-group">
                <label>Tenure Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "years", label: "Years" },
                    { value: "months", label: "Months" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTenureType(type.value);
                        setLoanTenure(type.value === "years" ? 20 : 240);
                      }}
                      className={`quick-select-btn ${tenureType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Interest Rate */}
              <div className="input-group">
                <label>Expected Interest Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8.5"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7, 8.5, 10, 12].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* FOIR Percentage */}
              <div className="input-group">
                <label>FOIR (Fixed Obligation to Income Ratio)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={foirPercentage}
                    onChange={(e) => setFoirPercentage(e.target.value)}
                    className="calc-input"
                    placeholder="50"
                    step="5"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[40, 50, 60, 65].map((foir) => (
                    <button
                      key={foir}
                      onClick={() => setFoirPercentage(foir)}
                      className="quick-select-btn secondary"
                    >
                      {foir}%
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateEligibility} className="calculate-btn">
                Check Eligibility
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Eligibility Summary
              </h2>

              <div className="result-card">
                {/* Eligible Loan Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Eligible Loan Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.eligibleLoanAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Maximum loan you can avail
                  </div>
                </div>

                {/* Available EMI */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Available EMI Capacity</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.availableEMI).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Monthly EMI you can afford
                  </div>
                </div>

                {/* Maximum Allowed EMI */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Maximum Allowed EMI</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.maxEMI).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Based on {foirPercentage}% FOIR
                  </div>
                </div>

                {/* Total Payment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Amount Payable</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalPayment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Over {loanTenure} {tenureLabel.toLowerCase()}
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">EMI Capacity Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Available EMI</span>
                      <span className="breakdown-percentage">
                        {(100 - parseFloat(result.utilizationPercentage)).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${100 - parseFloat(result.utilizationPercentage)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Existing Obligations</span>
                      <span className="breakdown-percentage">
                        {result.utilizationPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${result.utilizationPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does Loan Eligibility Work?</h3>
              <p>
                Loan eligibility is determined by your ability to repay the loan based on your monthly income and 
                existing financial obligations. Banks use the FOIR (Fixed Obligation to Income Ratio) to assess 
                how much of your income can be allocated toward loan repayments. Typically, lenders prefer that 
                your total EMIs (including the new loan) don't exceed 40-60% of your monthly income. This calculator 
                helps you understand your borrowing capacity by analyzing your income, existing obligations, desired 
                loan tenure, and prevailing interest rates to determine the maximum loan amount you can qualify for.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Eligibility Calculation Formula:</h4>
            <div className="formula-display">
              Eligible Loan = Available EMI × [(1 + r)^n - 1] / [r × (1 + r)^n]
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Available EMI</div>
                <div className="formula-param-value">(Income × FOIR%) - Existing EMI</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">FOIR</div>
                <div className="formula-param-value">Fixed Obligation to Income Ratio</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Monthly Interest Rate (Annual/12)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Loan Tenure in Months</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is FOIR and how does it affect my loan eligibility?</h5>
              <p>
                FOIR (Fixed Obligation to Income Ratio) is the percentage of your monthly income that can be 
                allocated toward loan repayments. Most banks maintain a FOIR between 40-60%, meaning your total 
                EMIs shouldn't exceed this percentage of your gross monthly income. A lower FOIR indicates better 
                repayment capacity. If your existing obligations are high, your FOIR utilization increases, reducing 
                your eligibility for new loans. Banks calculate FOIR by dividing your total monthly obligations 
                (including proposed loan EMI) by your gross monthly income.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I increase my loan eligibility amount?</h5>
              <p>
                You can increase eligibility by increasing your income (add co-applicant's income), reducing existing 
                debts and EMIs, choosing a longer loan tenure (reduces EMI but increases total interest), improving 
                your credit score (may get better interest rates), providing collateral for secured loans, reducing 
                discretionary expenses shown on bank statements, or maintaining a healthy bank balance. Some banks 
                also consider rental income, investment returns, and bonuses. Pre-closing existing loans significantly 
                improves eligibility by freeing up your FOIR capacity.
              </p>
            </div>

            <div className="faq-item">
              <h5>What documents are needed to check loan eligibility?</h5>
              <p>
                Banks typically require identity proof (Aadhaar, PAN card, passport), address proof (utility bills, 
                rental agreement), income proof (salary slips for last 3-6 months, Form 16, ITR for 2 years), bank 
                statements for 6 months, employment proof (offer letter, employment certificate), and existing loan 
                statements if any. Self-employed individuals need business registration documents, GST returns, profit 
                & loss statements, and balance sheets. Some banks may require property documents if applying for a 
                home loan, or quotations for vehicle/business loans.
              </p>
            </div>

            <div className="faq-item">
              <h5>Does checking eligibility affect my credit score?</h5>
              <p>
                Simply using an eligibility calculator doesn't affect your credit score as no credit inquiry is made. 
                However, when you formally apply for a loan and the bank checks your credit report, it creates a "hard 
                inquiry" which may temporarily reduce your score by a few points. Multiple loan applications within a 
                short period can significantly impact your score. It's advisable to check eligibility with calculators 
                first and apply only when confident. Some banks offer pre-approved loans which involve minimal credit 
                checks. Soft inquiries (by you checking your own score) don't affect credit ratings.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why do different banks offer different eligible loan amounts?</h5>
              <p>
                Banks have different FOIR thresholds (40-65%), risk assessment criteria, and policies for evaluating 
                income. Some banks consider only basic salary while others include bonuses and allowances. Interest 
                rates vary across banks affecting EMI calculations. Banks also weigh factors like credit score, job 
                stability, employer reputation, age, and existing relationship differently. Public sector banks may 
                have stricter norms than private/NBFCs. Your eligibility also depends on loan type - home loans 
                typically have higher FOIR limits (up to 60%) compared to personal loans (40-50%).
              </p>
            </div>

            <div className="faq-item">
              <h5>What if my calculated eligibility is less than what I need?</h5>
              <p>
                Consider adding a co-applicant (spouse, parent) to combine incomes and increase eligibility. Opt for 
                a longer tenure to reduce EMI and qualify for higher amounts (though total interest increases). Close 
                existing loans or credit card dues to free up FOIR capacity. Increase your down payment to reduce 
                the loan amount needed. Improve your credit score over time (takes 6-12 months) for better rates and 
                terms. Look for banks offering higher FOIR limits or special schemes. For home loans, check for 
                government subsidy schemes like PMAY which provide interest subsidies making loans more affordable.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanEligibilityCalculator;